import { useMutation, useQueryClient } from '@tanstack/react-query'
import { AxiosError } from 'axios'
import { muellesApi } from '@/services/api'
import type { CamionCreateRequest, CamionUpdateRequest } from '@/types'
import { CLAVE_CUADRO, CLAVE_SIN_MUELLE } from '@/hooks/useDockSchedule'

/** Un rechazo del backend, ya desenvuelto. **Código, no texto**: la pantalla es
 *  bilingüe y lo traduce por `muelles.errores.<codigo>`. */
export interface ErrorMuelle {
  codigo: string
  camion?: string
  carga?: string
  valores?: Record<string, string | number>
}

/** Saca el código del error de Axios. Un fallo de red o un 500 no traen código
 *  propio: se devuelve uno genérico en vez de inventar un motivo. */
export function errorMuelleDe(err: unknown): ErrorMuelle {
  const detalle = (err as AxiosError<{ detail?: ErrorMuelle }>)?.response?.data?.detail
  if (detalle?.codigo) return detalle
  return { codigo: 'error_inesperado' }
}

/**
 * Las cinco escrituras del cuadro.
 *
 * **Sin estados optimistas en esta versión, y es una decisión.** El servidor es
 * la autoridad sobre dos cosas que el cliente no puede saber: si el muelle se ha
 * solapado y si otro usuario ya se ha quedado esa carga. Pintar el camión y
 * retirarlo trescientos milisegundos después por un 409 es peor que esperar: el
 * usuario ve un cambio que no ha ocurrido y aprende a no fiarse de la pantalla.
 *
 * El hueco para el optimista **está marcado más abajo**, para cuando entre el
 * arrastrar y soltar: ahí sí hace falta, porque el usuario suelta la tarjeta y
 * tiene que verla donde la ha soltado. El patrón a copiar es el de
 * `useCapturas.reorder`: `onMutate` → `cancelQueries` → snapshot →
 * `setQueryData` → rollback en `onError` → `onSettled` invalida.
 */
export function useDockMutaciones(almacen?: string) {
  const qc = useQueryClient()

  // Invalidación POR PREFIJO, no por clave exacta. Mover un camión al martes
  // siguiente cambia una semana que no es la visible, y una invalidación exacta
  // dejaría esa otra semana mintiendo en la caché hasta que alguien recargara.
  const invalidar = () => {
    qc.invalidateQueries({ predicate: (q) => q.queryKey[0] === CLAVE_CUADRO })
    qc.invalidateQueries({ predicate: (q) => q.queryKey[0] === CLAVE_SIN_MUELLE })
    // La columna «Muelle» de la pantalla de cargas también se queda vieja.
    qc.invalidateQueries({ predicate: (q) => q.queryKey[0] === 'cargas-salida' })
  }

  const crearCamion = useMutation({
    mutationFn: (body: CamionCreateRequest) => muellesApi.crearCamion(body),
    onSuccess: invalidar,
  })

  const actualizarCamion = useMutation({
    mutationFn: (v: { id: string; body: CamionUpdateRequest }) =>
      muellesApi.actualizarCamion(v.id, v.body),
    onSuccess: invalidar,
  })

  const borrarCamion = useMutation({
    mutationFn: (id: string) => muellesApi.borrarCamion(id),
    onSuccess: invalidar,
  })

  const asignarCarga = useMutation({
    mutationFn: (v: { camionId: string; carga: string }) =>
      muellesApi.asignarCarga(v.camionId, v.carga),
    onSuccess: invalidar,
    // ── Hueco del arrastrar y soltar ────────────────────────────────────────
    // Cuando entre el drag & drop, aquí van `onMutate` (cancelar, guardar el
    // estado anterior y mover la tarjeta en la caché) y `onError` (devolverla a
    // su sitio). Hoy NO están: sin arrastre, el usuario no ha movido nada con la
    // mano y no hay nada que devolver a su sitio.
  })

  const quitarCarga = useMutation({
    mutationFn: (v: { camionId: string; carga: string }) =>
      muellesApi.quitarCarga(v.camionId, v.carga),
    onSuccess: invalidar,
  })

  void almacen   // el almacén acota la lectura, no la escritura

  return { crearCamion, actualizarCamion, borrarCamion, asignarCarga, quitarCarga, invalidar }
}

/**
 * Las mismas validaciones que hace el backend, en el cliente y **con los mismos
 * códigos**.
 *
 * No es duplicar por duplicar: así la mayoría de los rechazos no llega a viajar,
 * y sobre todo el mensaje que ve el usuario es idéntico venga de donde venga. Si
 * el cliente dijera «la hora no vale» y el servidor «fuera de jornada», serían
 * dos errores distintos para la misma cosa.
 *
 * Lo que el cliente NO puede validar es el solape contra camiones que no tenga
 * en la caché, ni si otro usuario se ha quedado la carga. Eso se deja al
 * servidor, que es quien lo sabe.
 */
export function validarTramoEnCliente(
  inicio: Date, fin: Date, jornada: { inicio: number; fin: number },
): ErrorMuelle | null {
  if (fin <= inicio) return { codigo: 'fecha_fin_anterior' }
  for (const [momento, campo] of [[inicio, 'inicio'], [fin, 'fin']] as const) {
    const minutos = momento.getHours() * 60 + momento.getMinutes()
    if (minutos < jornada.inicio * 60 || minutos > jornada.fin * 60) {
      return { codigo: 'fuera_de_horario', valores: { campo } }
    }
  }
  return null
}
